import * as THREE from 'three';
import { U } from './constants';
import { state } from './state';
import { refs } from './refs';
import { scene, envMap } from './scene';
import { striationV, striationH } from './textures';
import { buildCase } from './geometry';
import { refreshBackdrop } from './backdrop';

// ─── Assemble the whole book ───────────────────────────────────────
export function buildBook(): void {
  if (refs.bookGroup) {
    scene.remove(refs.bookGroup);
    refs.bookGroup.traverse(o => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
    });
  }
  const bookGroup = new THREE.Group();
  refs.bookGroup = bookGroup;

  const Wu = state.W * U,
    Hu = state.H * U,
    Su = state.S * U;
  const board = Math.max(0.012, 0.05 * Su); // cover board thickness
  const sq = state.squareF * 0.16 * U + 0.02 * U; // squares overhang (height & fore)
  const bulge = state.bulgeF * 0.55 * Su; // spine round depth

  const caseHu = Hu + 2 * sq; // case taller by squares
  const { artGeo, edgeGeo } = buildCase(Wu, caseHu, Su, bulge, board, {
    grooveDepth: state.grooveF * 0.06 * Su,
    grooveW: (0.1 + state.grooveWF * 0.4) * Su,
    bowAmt: state.bowF * 0.09 * Su,
    warpAmt: state.warpF * 0.04 * Su,
    rows: 16,
  });

  // Cover (art) material
  const artMat = new THREE.MeshStandardMaterial({
    map: refs.coverTex || null,
    color: refs.coverTex ? 0xffffff : 0x3a4a3a,
    roughness: 0.52,
    metalness: 0.0,
    envMap: envMap,
    envMapIntensity: state.reflInt,
    side: THREE.DoubleSide,
  });
  const artMesh = new THREE.Mesh(artGeo, artMat);
  artMesh.castShadow = true;
  artMesh.receiveShadow = true;
  bookGroup.add(artMesh);

  // Edge / inner material (turn-in look)
  const edgeMat = new THREE.MeshStandardMaterial({
    color: 0x20281f,
    roughness: 0.7,
    metalness: 0.0,
    envMap: envMap,
    envMapIntensity: state.reflInt * 0.5,
    side: THREE.DoubleSide,
  });
  const edgeMesh = new THREE.Mesh(edgeGeo, edgeMat);
  edgeMesh.castShadow = true;
  bookGroup.add(edgeMesh);

  // ── Page block (inset, with squares gap) ──
  const pageInsetFore = sq + board * 0.6; // pulled in from fore-edge
  const pageW = Wu - pageInsetFore; // from spine to (fore - inset)
  const pageH = Hu - 0.5 * sq; // slightly less than trim
  const pageThk = Su - 2 * board - 0.01 * U;
  const creamMat = new THREE.MeshStandardMaterial({
    color: 0xf3eee2,
    roughness: 0.95,
    envMap: envMap,
    envMapIntensity: 0.15,
  });
  const foreMat = new THREE.MeshStandardMaterial({
    map: striationV,
    roughness: 0.9,
    envMap: envMap,
    envMapIntensity: 0.12,
  });
  const topMat = new THREE.MeshStandardMaterial({
    map: striationH,
    roughness: 0.9,
    envMap: envMap,
    envMapIntensity: 0.12,
  });
  const spineSideMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.85 });
  // face order: +x, -x, +y, -y, +z, -z
  const pageMats = [foreMat, spineSideMat, topMat, topMat, creamMat, creamMat];
  const pageGeo = new THREE.BoxGeometry(pageW, pageH, pageThk, 8, 4, 4);
  // slight fore-edge convexity: push +x verts outward a touch by their y/z distance
  const pos = pageGeo.attributes.position;
  const halfW = pageW / 2;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    if (x > halfW - 1e-4) {
      const y = pos.getY(i) / (pageH / 2);
      const z = pos.getZ(i) / (pageThk / 2);
      const bulgeAmt = (1 - 0.5 * (y * y) - 0.5 * (z * z)) * 0.012 * U;
      pos.setX(i, x + bulgeAmt);
    }
  }
  pos.needsUpdate = true;
  pageGeo.computeVertexNormals();
  const pageMesh = new THREE.Mesh(pageGeo, pageMats);
  pageMesh.castShadow = true;
  pageMesh.receiveShadow = true;
  // position: spine side flush near spine, fore side inset
  pageMesh.position.set(-Wu / 2 + pageW / 2 + board * 0.5, 0, 0);
  bookGroup.add(pageMesh);

  // ── Headbands (head & tail of spine, tucked between pages and spine) ──
  // The bead lives in the cavity between the rounded spine and the squared
  // text block. That cavity's depth is (bulge − 0.5·board) and shrinks with
  // spine round; the bead radius must track it, otherwise at low spine round
  // the cavity collapses and the bead pokes through the now-flat spine.
  const pageSpineFaceX = -Wu / 2 + board * 0.5; // page block's spine-side face
  const spineCavity = bulge - 0.5 * board; // available depth at the spine crown
  const hbR = Math.min(Su * 0.07, spineCavity / 2); // bead radius, clamped to fit the cavity
  if (hbR > 0.0008 * U) {
    // skip when the spine is too flat to seat a visible bead
    const hbMat = new THREE.MeshStandardMaterial({
      color: 0x8a2f2f,
      roughness: 0.6,
      envMap: envMap,
      envMapIntensity: 0.2,
    });
    const hbGeo = new THREE.CylinderGeometry(hbR, hbR, pageThk * 0.9, 12);
    hbGeo.rotateX(Math.PI / 2); // axis along Z (across page thickness)
    const hbX = pageSpineFaceX - hbR; // nestled just into the spine cavity
    const hbY = pageH / 2 - hbR * 0.15; // right at the head/tail of the pages
    for (const sgn of [1, -1]) {
      const hb = new THREE.Mesh(hbGeo, hbMat);
      hb.position.set(hbX, sgn * hbY, 0);
      bookGroup.add(hb);
    }
  }

  // ── Ground level recorded for the backdrop ──
  state.groundY = -caseHu / 2 - 0.002;

  bookGroup.rotation.y = state.bookSpin;
  scene.add(bookGroup);
  refreshBackdrop();
}
